import { Component, OnDestroy, OnInit, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MediaObserver } from '@angular/flex-layout';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TitleService } from '~shared/services/title.service';
import { MEDIA_ALISES } from '~shared/constants/media.constant';
import { StoryblokService } from '~app/modules/storyblok/services';
import { Components } from '~app/modules/storyblok/constants';
import { SB_LANDING_PAGE_ROUTE } from '~app/modules/landing-page/constants';
import { ActivatedRoute } from '@angular/router';
import { StoryblokComponent } from 'storyblok-js-client';
import { getCountryDomainCode } from '~server/utils/country-detection.util';
import { InputsType } from 'ng-dynamic-component';

@Component({
  selector: 'app-landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
})
export class LandingPageComponent implements OnInit, OnDestroy {
  isMobile = false;
  isMobileOrTablet = false;
  isDesktop = false;
  isTablet = false;
  isLandScapeTablet = false;
  landingPageTitle: string;

  // Storyblok
  storyBody: Array<StoryblokComponent<string>> = [];
  showTitle: boolean = true;
  components = Components;
  storyEditable: string;
  storyId: number;

  private unsubscribe$ = new Subject();
  private get landingPageSlug(): string {
    const [path] = window.location.href.split('?');
    return path.split('/').pop();
  }

  constructor(
    private titleService: TitleService,
    private mediaObserver: MediaObserver,
    private storyblokService: StoryblokService,
    private activatedRoute: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.checkContentOnStoryblokEdit();
  }

  ngOnInit(): void {
    this.activatedRoute.params.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.initializeMediaObservable();
      this.loadStoryblokDynamicContent(this.landingPageSlug);
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private initializeMediaObservable(): void {
    this.mediaObserver
      .asObservable()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.isMobile = this.mediaObserver.isActive(MEDIA_ALISES.mobile);
        this.isMobileOrTablet = this.isMobile || this.mediaObserver.isActive(MEDIA_ALISES.tablet);
        this.isDesktop = this.mediaObserver.isActive(MEDIA_ALISES.desktop);
        this.isTablet = this.mediaObserver.isActive(MEDIA_ALISES.tablet);
        this.isLandScapeTablet = this.mediaObserver.isActive(MEDIA_ALISES.tabletLandscape);
      });
  }

  buildInputs(blokInputs: InputsType): InputsType {
    return {
      ...blokInputs,
      ...{
        isMobile: this.isMobile,
        isTablet: this.isTablet,
        isDesktop: this.isDesktop,
        isLandScapeTablet: this.isLandScapeTablet,
      },
    };
  }

  private async loadStoryblokDynamicContent(pageSlug: string): Promise<void> {
    if (isPlatformBrowser(this.platformId)) {
      try {
        const market = getCountryDomainCode();
        const storySlug = `${SB_LANDING_PAGE_ROUTE}/${pageSlug}`.replace('{market}', market);
        const { story } = await this.storyblokService
          .getStoryByHttp(storySlug).toPromise();
        this.storyId = story.id;
        this.assignStoryData(story);
      } catch (e) {
        console.error(e);
      }
    }
  }

  private checkContentOnStoryblokEdit(): void {
    window?.storyblok?.on('input', (event) => {
      window.storyblok.addComments(event.story.content, String(event.story.id));
      if (event.story.id === this.storyId) {
        this.assignStoryData(event.story);
      }
    });
  }

  private assignStoryData(story): void {
    this.landingPageTitle = story.name;
    const { body, displayPageTitle } = story.content;
    this.storyBody = [...[], ...body];
    this.showTitle = displayPageTitle;
    this.storyEditable = story.content._editable;
    this.titleService.setTitle([story.name, { title: 'TITLE.PAGE.LANDING_PAGE' }]);
  }

  getPadding(): string {
    if (!this.showTitle) {
      return !this.isMobileOrTablet ? '0 20px 20px' : '0 32px 32px'
    }
    return !this.isMobileOrTablet ? '20px' : '32px'
  }
}
